/**
 * External dependencies
 */
import Headroom from 'headroom.js';
/**
 * Internal dependencies
 */
import { enable, disable } from '../../../assets/js/base/_tabIndex.js';

class MastheadItem {
	constructor( node, controller, parent = null ) {
		this.node = node;
		this.controller = controller;
		this.parent = parent;

		if ( ! this.node ) {
			return;
		}

		this.dropdown = this.node.querySelector( '.masthead__dropdown' );

		const items = this.dropdown ? Array.from( this.dropdown.querySelectorAll( ':scope > .masthead__items > .masthead__item--dropdown' ) ) : [];
		this.items = items.map( ( child ) => new MastheadItem( child, this.controller, this ) );

		// stores the timeout id of the enter hover intent
		this.enterTimeout = null;

		// stores the timeout of the leave hover intent
		this.leaveTimeout = null;

		// is the current item active
		this.active = false;

		// reference the direct child link in case we need to preventDefault on click
		this.link = this.node.querySelector( ':scope > .masthead__link' );

		// does this item have a back button on the mobile menu
		this.back = this.node.querySelector( '.masthead__back' );

		// what types of pointer events are 'hoverable'
		this.hoverTypes = [
			'mouse',
			'pen',
		];

		// trigger the enter hover intent depending on the pointer type
		const onPointerEnter = ( e ) => {
			if ( this.hoverTypes.includes( e.pointerType ) && 'hover' === this.controller.getPointerType() ) {
				if ( typeof this.leaveTimeout === 'number' ) {
					window.clearTimeout( this.leaveTimeout );
					this.leaveTimeout = null;
				}

				this.enterTimeout = setTimeout( () => this.activate(), this.controller.hoverDelay );
			}
		};

		// trigger the leave hover intent depending on the pointer type
		const onPointerLeave = ( e ) => {
			if ( this.hoverTypes.includes( e.pointerType ) && 'hover' === this.controller.getPointerType() ) {
				if ( typeof this.enterTimeout === 'number' ) {
					window.clearTimeout( this.enterTimeout );
					this.enterTimeout = null;
				}

				this.leaveTimeout = window.setTimeout( () => this.deactivate(), this.controller.hoverDelay );
			}
		};

		// store the last pointer type on pointerup so we can potentially preventDefault onClick
		const onPointerUp = ( e ) => {
			this.pointerType = e.pointerType ?? null;
		};

		// if not in hover mode, or we're not a hoverable type, simply toggle instead of following the link
		const onClick = ( e ) => {
			if ( 'hover' !== this.controller.getPointerType() || ! this.hoverTypes.includes( this.pointerType ) ) {
				e.preventDefault();
				this.toggle();
			}

			if ( this.pointerType ) {
				delete this.pointerType;
			}
		};

		// when a element outside of this node is focused, close this menu
		const onFocus = () => {
			let target = document.activeElement;

			while ( target && target.parentNode ) {
				if ( target === this.node ) {
					return;
				}

				target = target.parentNode;
			}

			if ( this.controller.shouldDeactivateOtherDropdowns() ) {
				this.deactivate();
			}
		};

		this.node.addEventListener( 'pointerenter', onPointerEnter );
		this.node.addEventListener( 'pointerleave', onPointerLeave );
		this.node.addEventListener( 'pointerup', onPointerUp );
		document.body.addEventListener( 'focus', onFocus, true );

		if ( this.back ) {
			this.back.addEventListener( 'click', this.deactivate.bind( this ) );
		}

		if ( this.link ) {
			this.link.addEventListener( 'click', onClick );
		}
	}

	/**
	 * Toggle this dropdown menu state
	 */
	toggle() {
		this.active ? this.deactivate() : this.activate();
	}

	/**
	 * Activate this dropdown menu state
	 */
	activate() {
		if ( ! this.active ) {
			this.active = true;
			this.controller.deactivate(
				this.parent
					? [
							this,
							this.parent,
					  ]
					: [ this ]
			);
			this.node.classList.add( this.controller.parentActiveClass );
			this.link.setAttribute( 'aria-expanded', '' );
			this.node.dispatchEvent( new Event( 'masthead:activate' ) );

			if ( ! this.controller.mediaQuery.matches ) {
				const box = this.dropdown.getBoundingClientRect();

				if ( box.left < 0 ) {
					this.dropdown.classList.add( 'masthead__dropdown--off-left' );
				} else if ( box.right > window.innerWidth ) {
					this.dropdown.classList.add( 'masthead__dropdown--off-right' );
				}
			}
		}
	}

	/**
	 * Deactivate this dropdown menu state
	 */
	deactivate() {
		if ( this.active ) {
			this.active = false;
			this.node.classList.remove( this.controller.parentActiveClass );
			this.node.dispatchEvent( new Event( 'masthead:deactivate' ) );
			this.link.removeAttribute( 'aria-expanded' );

			if ( this.dropdown ) {
				this.dropdown.classList.remove( 'masthead__dropdown--off-left', 'masthead__dropdown--off-right' );
			}
		}
	}
}

class Masthead {
	constructor( node = '.masthead' ) {
		this.node = typeof node === 'string' ? document.querySelector( node ) : node;

		if ( ! this.node ) {
			return;
		}

		// no mode by default (can be search|menu)
		this.mode = null;

		// what mouse/pen mode do we want to operate under - hover or click
		this.pointerType = this.node.dataset.pointerType || 'hover';

		// the delay used for hover-intent
		this.hoverDelay = 200;

		// mobile/desktop breakpoint
		this.breakpoint = parseInt( this.node.dataset.breakpoint, 10 );

		// the item active class
		this.parentActiveClass = 'masthead__item--active';

		this.displayModes = [
			'mobile',
			'desktop',
		];

		this.mobileDropdownModes = [
			'flyout',
			'collapse',
		];

		// node references for interaction
		this.layout = this.node.querySelector( '.masthead__layout' );
		this.utility = this.node.querySelector( '.masthead__utility' );
		this.primary = this.node.querySelector( '.masthead__primary' );
		this.logo = this.primary ? this.primary.querySelector( '.masthead__logo' ) : null;
		this.primaryNav = this.primary ? this.primary.querySelector( '.masthead__navigation' ) : null;
		this.actions = this.primary ? this.primary.querySelector( '.masthead__actions' ) : null;
		this.menuToggle = this.node.querySelector( '.masthead__menu-toggle' );
		this.searchform = this.node.querySelector( '.masthead__searchform' );
		this.searchInput = this.searchform ? this.searchform.querySelector( 'input[name="s"]' ) : null;
		this.searchToggle = this.node.querySelector( '.masthead__search-toggle' );
		this.closeIcon = this.node.querySelector( '.masthead__close-icon' );

		// at what media query is the mobile menu triggered - update this value to match whatever is used in _masthead.scss
		this.mediaQuery = window.matchMedia( `(max-width: ${ this.breakpoint || 768 }px)` );

		// initialize menu items that are parents
		this.items = Array.from( this.node.querySelectorAll( '.masthead__item--dropdown.masthead__item--depth-0' ) ).map( ( node ) => {
			return new MastheadItem( node, this );
		} );

		if ( this.node.classList.contains( 'masthead--sticky' ) || this.node.classList.contains( 'masthead--headroom' ) ) {
			// headroom sticky hide configuration - @see https://www.npmjs.com/package/headroom.js
			this.headroom = new Headroom( this.node, {
				tolerance: {
					up: 7,
					down: 5,
				},
				classes: {
					initial: 'masthead',
					pinned: 'masthead--pinned',
					unpinned: 'masthead--unpinned',
					top: 'masthead--top',
					notTop: 'masthead--not-top',
					bottom: 'masthead--bottom',
					notBottom: 'masthead--not-bottom',
					frozen: 'masthead--frozen',
				},
			} );
			this.headroom.init();
			this.headroom.update( this.headroom );
		}

		// deactivate the mode of the menu on escape key
		const onKeyUp = ( e ) => {
			switch ( e.key ) {
				case 'Escape':
					if ( this.mode ) {
						this.setMode( '' );
					}
					break;
			}
		};

		// deactivate mode when clicking outside of the masthead
		const onClick = ( e ) => {
			let target = e.target;

			while ( target.parentNode ) {
				if ( target === this.node ) {
					return;
				}

				target = target.parentNode;
			}

			this.deactivate();
		};

		const onMediaQueryChange = () => {
			this.setDisplayMode();
			this.deactivate();
		};

		this.mediaQuery.addEventListener( 'change', onMediaQueryChange );
		this.setDisplayMode();
		this.getMobileDropdownMode();

		if ( this.searchform ) {
			disable( null, this.searchform );
		}

		this.node.addEventListener( 'keyup', onKeyUp );

		if ( this.searchToggle ) {
			this.searchToggle.addEventListener( 'click', ( e ) => {
				e.stopPropagation();
				this.toggleMode( 'search' );
			} );
		}

		if ( this.menuToggle ) {
			this.menuToggle.addEventListener( 'click', ( e ) => {
				e.stopPropagation();
				this.toggleMode( 'menu' );
			} );
		}

		if ( this.closeIcon ) {
			this.closeIcon.addEventListener( 'click', ( e ) => {
				e.stopPropagation();
				this.setMode();
			} );
		}

		if ( this.layout ) {
			this.layout.scrollTo( 0, 0 );
		}

		document.body.addEventListener( 'click', onClick );

		// Add ready class after full initialization
		this.node.classList.add( 'masthead--ready' );
	}

	/**
	 * Deactivate all dropdown menus (optionally exclude items)
	 *
	 * @param MastheadItem[] exclude
	 * @param exclude
	 */
	deactivate( exclude ) {
		if ( this.shouldDeactivateOtherDropdowns() ) {
			this.items.forEach( ( item ) => {
				if ( item.active && ( ! exclude || ! exclude.includes( item ) ) ) {
					item.deactivate();
				}
			} );
		}

		if ( ! this.mediaQuery.matches ) {
			this.setMode( '' );
		}
	}

	/**
	 * Set the interactive mode of the masthead
	 *
	 * @param {string} mode search|menu
	 * @return void
	 */
	setMode( mode = '' ) {
		if ( mode === this.mode ) {
			return;
		}

		this.node.classList.remove( `masthead--mode-${ this.mode }` );

		if ( mode ) {
			this.mode = mode;
			this.node.classList.add( 'masthead--mode', `masthead--mode-${ this.mode }` );

			if ( this.headroom ) {
				this.headroom.freeze();
			}
		} else {
			this.mode = '';
			this.node.classList.remove( 'masthead--mode' );

			if ( this.headroom ) {
				this.headroom.unfreeze();
			}
		}

		if ( this.searchform ) {
			if ( 'search' === mode ) {
				enable( null, this.searchform );

				if ( this.searchInput ) {
					this.searchInput.focus();
				}
			} else {
				disable( null, this.searchform );
			}
		}

		this.node.scrollTo( 0, 0 );
		this.node.dispatchEvent( new Event( 'masthead:mode', { detail: this } ) );
	}

	/**
	 * Toggle the interactive mode of the masthead
	 *
	 * @param {string} mode search|menu
	 */
	toggleMode( mode ) {
		this.setMode( this.mode === mode ? '' : mode );
	}

	/**
	 * Gets the pointerType of how the dropdowns should react to pointer events
	 *
	 * - mobile view should always be click based even with mouse/pen pointer events
	 * - update the breakpoint
	 *
	 * @return {string} hover|click
	 */
	getPointerType() {
		if ( 'hover' === this.pointerType && 'mobile' === this.getDisplayMode() ) {
			return 'click';
		}

		return this.pointerType;
	}

	getDisplayMode() {
		return this.mediaQuery.matches ? 'mobile' : 'desktop';
	}

	setDisplayMode( displayMode ) {
		if ( ! displayMode || ! this.displayModes.includes( displayMode ) ) {
			displayMode = this.getDisplayMode();
		}

		const audienceButtons = this.node?.querySelector( '.wp-block-wdg-masthead-audience-buttons' );

		switch ( displayMode ) {
			case 'mobile':
				this.node.classList.add( 'masthead--mobile' );
				this.node.classList.remove( 'masthead--desktop' );

				// utility is ordered last on mobile
				if ( this.utility && this.primary ) {
					this.utility.parentNode.appendChild( this.utility );
				}

				// Move audience buttons into primary on mobile
				if ( audienceButtons && this.primary ) {
					this.primary.appendChild( audienceButtons );
				}

				if ( this.primary ) {
					// re-order primary items on mobile
					[
						this.logo,
						this.actions,
						this.primaryNav,
						this.searchform,
					].forEach( ( node ) => {
						if ( node ) {
							this.primary.appendChild( node );
						}
					} );
				}
				break;
			case 'desktop':
				this.node.classList.add( 'masthead--desktop' );
				this.node.classList.remove( 'masthead--mobile' );

				// primary is ordered last on desktop
				if ( this.utility && this.primary ) {
					this.primary.parentNode.appendChild( this.primary );
				}

				// Move audience buttons back to utility on desktop
				if ( audienceButtons && this.utility ) {
					this.utility.insertBefore( audienceButtons, this.utility.firstChild );
				}

				if ( this.primary ) {
					// re-order primary items on desktop
					[
						this.logo,
						this.primaryNav,
						this.searchform,
						this.actions,
					].forEach( ( node ) => {
						if ( node ) {
							this.primary.appendChild( node );
						}
					} );
				}
				break;
		}
	}

	getMobileDropdownMode() {
		if ( ! this.mobileDropdownMode ) {
			for ( const mode of this.mobileDropdownModes ) {
				if ( this.node.classList.contains( `masthead--mobile-${ mode }` ) ) {
					this.mobileDropdownMode = mode;
					break;
				}
			}
		}

		return this.mobileDropdownMode;
	}

	shouldDeactivateOtherDropdowns() {
		if ( 'mobile' === this.getDisplayMode() && 'collapse' === this.getMobileDropdownMode() ) {
			return false;
		}

		return true;
	}
}

export default new Masthead();
